import {
  Component,
  ElementRef,
  ViewChild,
  Input,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';

import * as d3 from 'd3';
import { ThemeService } from '../../../../core/services/theme.service';
import { Subscription } from 'rxjs';

export interface MultiBarData {
  category: string;
  groups: {
    label: string;
    value: number;
    color: string;
  }[];
}

@Component({
  selector: 'app-multi-horizontal-bar-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './multi-horizontal-bar-chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./multi-horizontal-bar-chart.component.scss'],
})
export class MultiHorizontalBarChartComponent
  implements AfterViewInit, OnChanges {
  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;

  @Input() data: MultiBarData[] = [];
  @Input() showXAxisGrid: boolean = true;
  @Input() showYAxisGrid: boolean = false;
  @Input() showXAxisLabel: boolean = true;
  @Input() showYAxisLabel: boolean = true;
  @Input() showXAxisLine: boolean = true;
  @Input() showYAxisLine: boolean = true;
  @Input() showBarsValue: boolean = true;
  @Input() showLegend: boolean = true;
  @Input() leftMargin: number = 120;
  @Input() showTooltip: boolean = true;
  @Input() currentTheme: 'light' | 'dark' = 'dark';
  @Input() height: number = 0;
  @Input() barHeight: number = 24;
  @Input() barSpacing: number = 6;
  @Input() categoryPadding: number = 10;
  @Input() legendGap: number = 120; // Maintain backward compatibility
  @Input() showStripes: boolean = false;
  @Input() valueType: '%' | 'number' | '' = '';

  private themeService = inject(ThemeService);
  private resizeObserver!: ResizeObserver;
  private themeSubscription?: Subscription;

  ngAfterViewInit() {
    this.resizeObserver = new ResizeObserver(() => this.drawChart());
    this.resizeObserver.observe(this.chartContainer.nativeElement);
    this.currentTheme = this.themeService.getTheme();
    this.themeSubscription = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
      this.drawChart();
    });
    this.drawChart();
  }

  ngOnDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['data'] ||
      changes['showXAxisGrid'] ||
      changes['showYAxisGrid'] ||
      changes['showBarsValue'] ||
      changes['showXAxisLabel'] ||
      changes['showYAxisLabel'] ||
      changes['showXAxisLine'] ||
      changes['showYAxisLine'] ||
      changes['barSpacing'] ||
      changes['categoryPadding'] ||
      changes['height'] ||
      changes['showTooltip']
    ) {
      this.drawChart();
    }
  }

  private drawChart() {
    if (!this.data?.length) return;

    const container: HTMLElement = this.chartContainer.nativeElement;
    const parent: HTMLElement | null = container.parentElement;
    if (!parent) return;

    // Clear old SVG and any existing body-level tooltip
    d3.select(container).selectAll('*').remove();
    d3.select('body').selectAll('.mhb-chart-tooltip').remove();

    const colors = this.getColors();

    // Calculate dynamic right margin based on max value label length
    const maxValForMargin =
      d3.max(this.data.flatMap((d) => d.groups.map((g) => g.value))) || 0;
    const maxLabelLength = maxValForMargin.toLocaleString().length;
    const estimatedLabelWidth = maxLabelLength * 8 + 15; // 8px per char + offset

    const margin = {
      top: 20,
      right: this.showBarsValue ? Math.max(40, estimatedLabelWidth) : 40,
      bottom: this.showLegend ? 60 : 40,
      left: this.leftMargin,
    };
    const width = container.clientWidth - margin.left - margin.right;

    // Calculate dynamic height based on number of categories and bars
    const barSpacing = this.barSpacing;
    const groupSpacing = 0;
    const categoryPadding = this.categoryPadding; // Padding at top and bottom of each category
    const maxBarsPerCategory = Math.max(
      ...this.data.map((d) => d.groups.length)
    );
    const barHeight = this.barHeight;
    const categoryHeight =
      barHeight * maxBarsPerCategory +
      barSpacing * (maxBarsPerCategory - 1) +
      categoryPadding * 2;
    const totalHeight =
      this.data.length * categoryHeight + (this.data.length - 1) * groupSpacing;
    const height = this.height > 0 ? this.height : totalHeight;

    const svgElement = d3
      .select(container)
      .append('svg')
      .attr('width', '100%')
      .attr('height', height + margin.top + margin.bottom);

    // Filter patterns and gradients
    if (this.showStripes) {
      const defs = svgElement.append('defs');
      
      // Diagonal stripes pattern
      const patternSize = 8;
      const pattern = defs.append('pattern')
        .attr('id', 'diagonal-stripes')
        .attr('patternUnits', 'userSpaceOnUse')
        .attr('width', patternSize)
        .attr('height', patternSize)
        .attr('patternTransform', 'rotate(45)');

      pattern.append('rect')
        .attr('width', patternSize)
        .attr('height', patternSize)
        .attr('fill', 'transparent');

      pattern.append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', 0)
        .attr('y2', patternSize)
        .attr('stroke', 'rgba(255, 255, 255, 0.4)')
        .attr('stroke-width', 2);
    }

    const svg = svgElement.append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Tooltip appended to body to avoid overflow clipping
    let tooltip: any;
    if (this.showTooltip) {
      tooltip = d3
        .select('body')
        .append('div')
        .attr('class', 'mhb-chart-tooltip')
        .style('opacity', '0')
        .style('position', 'fixed')
        .style('pointer-events', 'none')
        .style('background', colors.tooltipBg)
        .style('border', colors.border)
        .style('border-radius', '12px')
        .style('padding', '10px 14px')
        .style('box-shadow', colors.shadow)
        .style('color', colors.text)
        .style('font-family', 'Inter, sans-serif')
        .style('white-space', 'nowrap')
        .style('transition', 'opacity 0.2s ease, transform 0.2s ease')
        .style('backdrop-filter', 'blur(8px)')
        .style('z-index', '9999');
    }

    // Define Scales
    const y0 = d3
      .scaleBand()
      .domain(this.data.map((d) => d.category))
      .range([0, height])
      .paddingInner(0.2)
      .paddingOuter(0.1);

    const groupLabels = Array.from(
      new Set(this.data.flatMap((d) => d.groups.map((g) => g.label)))
    );

    const y1 = d3
      .scaleBand()
      .domain(groupLabels)
      .range([0, y0.bandwidth()])
      .padding(this.barSpacing / y0.bandwidth());

    // Find max value for x scale
    const maxVal =
      d3.max(this.data.flatMap((d) => d.groups.map((g) => g.value))) || 1;

    // Generate ticks and ensure domain covers the last tick for perfect grid alignment
    const xTemp = d3.scaleLinear().domain([0, maxVal]).nice();
    const ticks = xTemp.ticks(6);
    if (ticks[ticks.length - 1] < maxVal) {
      const step = ticks[1] - ticks[0];
      ticks.push(ticks[ticks.length - 1] + step);
    }
    const x = d3
      .scaleLinear()
      .domain([0, ticks[ticks.length - 1]])
      .range([0, width]);

    // Draw X-Axis Line (Bottom Spine)
    if (this.showXAxisLine) {
      svg
        .append('line')
        .attr('class', 'x-axis-line')
        .attr('x1', 0)
        .attr('x2', width)
        .attr('y1', height)
        .attr('y2', height)
        .attr('stroke', colors.axisBaseLine)
        .attr('stroke-width', 1);
    }

    // Draw Y-Axis Line (Left Spine)
    if (this.showYAxisLine) {
      svg
        .append('line')
        .attr('class', 'y-axis-line')
        .attr('x1', 0)
        .attr('x2', 0)
        .attr('y1', 0)
        .attr('y2', height)
        .attr('stroke', colors.axisBaseLine)
        .attr('stroke-width', 1);
    }

    // X-axis grid lines (vertical)
    if (this.showXAxisGrid) {
      const xGrid = d3
        .axisBottom(x)
        .tickSize(-height)
        .tickFormat(() => '')
        .tickValues(ticks);

      svg
        .append('g')
        .attr('class', 'x-grid')
        .attr('transform', `translate(0, ${height})`)
        .call(xGrid)
        .selectAll('line')
        .attr('stroke', colors.axisLine)
        .attr('stroke-dasharray', '4 4')
        .attr('stroke-width', 1);

      svg.select('.x-grid .domain').remove();
    }

    // Y-axis grid lines (horizontal)
    if (this.showYAxisGrid) {
      this.data.forEach((d) => {
        const yPos = y0(d.category) || 0;

        svg
          .append('line')
          .attr('class', 'y-grid-line')
          .attr('x1', 0)
          .attr('x2', width)
          .attr('y1', yPos)
          .attr('y2', yPos)
          .attr('stroke', colors.axisLine)
          .attr('stroke-dasharray', '4 4')
          .attr('stroke-width', 1)
          .attr('opacity', 0.6);
      });

      // Draw final horizontal line at the bottom
      svg
        .append('line')
        .attr('class', 'y-grid-line')
        .attr('x1', 0)
        .attr('x2', width)
        .attr('y1', height)
        .attr('y2', height)
        .attr('stroke', colors.axisLine)
        .attr('stroke-dasharray', '4 4')
        .attr('stroke-width', 1)
        .attr('opacity', 0.6);
    }

    // Y-axis labels (category names)
    if (this.showYAxisLabel) {
      this.data.forEach((d) => {
        const yPos = (y0(d.category) || 0) + y0.bandwidth() / 2;

        svg
          .append('text')
          .attr('x', -10)
          .attr('y', yPos)
          .attr('text-anchor', 'end')
          .attr('dominant-baseline', 'middle')
          .style('font-size', '14px')
          .style('font-weight', '500')
          .style('fill', colors.labelColor)
          .text(d.category);
      });
    }

    // Helper function for generating bar path
    const getBarPath = (y: number, w: number, h: number) => {
      const r = Math.min(8, h / 2);
      if (w < r) {
        return `M 0 ${y} H ${w} V ${y + h} H 0 Z`;
      }
      return `
        M 0 ${y}
        H ${w - r}
        Q ${w} ${y} ${w} ${y + r}
        V ${y + h - r}
        Q ${w} ${y + h} ${w - r} ${y + h}
        H 0
        Z
      `;
    };

    // Draw bars
    this.data.forEach((categoryData) => {
      const categoryY = y0(categoryData.category) || 0;

      categoryData.groups.forEach((group) => {
        const barY = categoryY + (y1(group.label) || 0);
        const barHeight = y1.bandwidth();
        const barWidth = group.value > 0 ? Math.max(4, x(group.value)) : 0;

        const bar = svg
          .append('path')
          .attr('class', 'bar')
          .attr('d', () => getBarPath(barY, barWidth, barHeight))
          .attr('fill', group.color)
          .style('cursor', this.showTooltip ? 'pointer' : 'default');

        // Stripe overlay
        if (this.showStripes && barWidth > 0) {
          svg.append('path')
            .attr('class', 'bar-stripes')
            .attr('d', () => getBarPath(barY, barWidth, barHeight))
            .attr('fill', 'url(#diagonal-stripes)')
            .style('pointer-events', 'none');
        }

        if (this.showTooltip) {
          bar
            .on('mouseenter', function () {
              const expansion = 4;
              d3.select(this)
                .transition()
                .duration(200)
                .attr(
                  'd',
                  getBarPath(
                    barY - expansion / 2,
                    barWidth,
                    barHeight + expansion
                  )
                );
            })
            .on('mousemove', (event: MouseEvent) => {
              const tooltipElement = tooltip.node() as HTMLElement;
              if (!tooltipElement) return;

              const tooltipRect = tooltipElement.getBoundingClientRect();
              let left = event.clientX + 16;
              let top = event.clientY - 10;

              // Boundary check (right side of viewport)
              if (left + tooltipRect.width > window.innerWidth) {
                left = event.clientX - tooltipRect.width - 16;
              }
              // Boundary check (bottom of viewport)
              if (top + tooltipRect.height > window.innerHeight) {
                top = event.clientY - tooltipRect.height - 10;
              }

              tooltip
                .style('opacity', '1')
                .style('visibility', 'visible')
                .style('left', `${left}px`)
                .style('top', `${top}px`)
                .html(
                  `
                  <div style="display:flex;flex-direction:column;gap:4px;">
                    <div style="font-size:12px;opacity:0.8;color:${colors.labelColor};">
                      ${categoryData.category}
                    </div>
                    <div style="display:flex;align-items:center;gap:8px;">
                      <div style="width:8px;height:8px;border-radius:2px;background:${group.color};"></div>
                      <div style="font-size:14px;font-weight:500;">
                        ${group.label}: <span style="font-weight:700;">${group.value.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                `
                );
            })
            .on('mouseleave', function () {
              d3.select(this)
                .transition()
                .duration(200)
                .attr('d', getBarPath(barY, barWidth, barHeight));

              tooltip
                .style('opacity', '0')
                .style('visibility', 'hidden');
            });
        }

        // Add value labels on bars
        if (this.showBarsValue && group.value > 0) {
          svg
            .append('text')
            .attr('x', barWidth + 8)
            .attr('y', barY + barHeight / 2)
            .attr('dominant-baseline', 'middle')
            .style('font-size', '12px')
            .style('fill', colors.text)
            .style('font-weight', '500')
            .text(group.value.toLocaleString());
        }
      });
    });

    // X-axis (bottom numbers)
    if (this.showXAxisLabel) {
      const xAxisScale = d3.axisBottom(x).ticks(6);
      
      if (this.valueType) {
        xAxisScale.tickFormat((d: any) => `${d}${this.valueType}`);
      }

      const xAxis = svg
        .append('g')
        .attr('class', 'x-axis')
        .attr('transform', `translate(0, ${height})`)
        .call(xAxisScale);

      xAxis
        .selectAll('text')
        .style('fill', colors.labelColor)
        .style('font-size', '12px');

      xAxis.select('path.domain').remove();
    }

    // Legend
    if (this.showLegend && groupLabels.length > 0) {
      const legendY = height + margin.bottom - 20;
      const itemPadding = 10;
      const squareWidth = 20;
      const textGap = 6;
      const fontSize = 12;

      // Create a temporary group to measure text widths
      const tempGroup = svg.append('g').style('visibility', 'hidden');
      const measuredItems = groupLabels.map((label, i) => {
        const group = this.data[0].groups.find((g) => g.label === label);
        const textToMeasure = tempGroup.append('text').style('font-size', `${fontSize}px`).text(label);
        const textWidth = (textToMeasure.node() as SVGTextElement).getComputedTextLength() || 0;
        
        // Calculate minimum width required to show square + text + padding
        const minRequiredWidth = squareWidth + textGap + textWidth + itemPadding;
        
        return { 
          label,
          color: group?.color || '#ccc',
          width: Math.max(minRequiredWidth, this.legendGap)
        };
      });
      tempGroup.remove();

      const totalLegendWidth = measuredItems.reduce((acc, item) => acc + item.width, 0);
      let currentX = width / 2 - totalLegendWidth / 2;

      measuredItems.forEach((d) => {
        const g = svg.append('g')
          .attr('transform', `translate(${currentX}, ${legendY})`);

        // Color square
        g.append('rect')
          .attr('width', squareWidth)
          .attr('height', 15)
          .attr('rx', 2)
          .attr('fill', d.color);

        // Label
        g.append('text')
          .attr('x', squareWidth + textGap)
          .attr('y', 8)
          .attr('dominant-baseline', 'middle')
          .style('font-size', `${fontSize}px`)
          .style('fill', d.color)
          .style('font-weight', '500')
          .text(d.label);

        // Stripe overlay in legend
        if (this.showStripes) {
          g.append('rect')
            .attr('width', squareWidth)
            .attr('height', 15)
            .attr('rx', 2)
            .attr('fill', 'url(#diagonal-stripes)')
            .style('pointer-events', 'none');
        }

        currentX += d.width;
      });
    }
  }

  private getColors() {
    const isDark = this.currentTheme === 'dark';

    return {
      background: isDark ? '#232327' : '#F5F5F5',
      tooltipBg: isDark ? 'rgba(35, 35, 39, 0.9)' : 'rgba(255, 255, 255, 0.9)',
      text: isDark ? '#ffffff' : '#141437',
      shadow: isDark
        ? '0px 4px 20px rgba(0,0,0,0.35)'
        : '0px 4px 20px rgba(0,0,0,0.15)',
      border: isDark ? '1px solid #2A2A2E' : '1px solid #EAEAEA',
      subtitleColor: isDark ? '#A1A1AA' : '#7D7D93',
      axisLine: isDark ? '#ffffff33' : '#00000022',
      axisBaseLine: isDark ? '#7979CA' : '#BFBFC7',
      labelColor: isDark ? '#A1A1AA' : '#7D7D93',
    };
  }
}
